import { Button, InputControl, TextInput } from "@growtherapy/sprout-ui";
import { ChangeEvent, useCallback, useState } from "react";
import { AVPermissionCallout } from "./AVPermissionCallout";
import {
  EMPTY_INPUT_ERROR,
  MAX_NAME_LENGTH,
  TOO_LONG_INPUT_ERROR,
  UNSAFE_CHARACTERS_INPUT_ERROR,
} from "./constants";
import {
  containsNonAsciiCharacters,
  isOverCharacterLimit,
  shouldWarnForUnsafeCharacters,
} from "./utils";
import { twMerge } from "tailwind-merge";
import { useVisitorName } from "./useVisitorName";
import { useEnterWaitingRoomCallback } from "../nav/useEnterWaitingRoomCallback";
import { useAuth0 } from "@auth0/auth0-react";
import { useGetCurrentPatientQuery } from "../../generated/graphql";
import { useParams } from "react-router-dom";

type WrapperPassThroughProps = {
  buttonLabel?: string;
  buttonClassName?: string;
  className?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  shouldDisableSubmitWhenInvalid?: boolean;
  showAVPermissionCallout?: boolean;
};
export interface InputControlsProps extends WrapperPassThroughProps {
  children?: React.ReactNode;
  onEnterWaitingRoom: (visitorName: string) => void;
  setVisitorName: (visitorName: string) => void;
  visitorName: string;
  preferredName?: string;
}

export function InputControls({
  buttonLabel = "Enter waiting room",
  buttonClassName,
  className,
  onChange,
  onEnterWaitingRoom,
  setVisitorName,
  shouldDisableSubmitWhenInvalid = false,
  showAVPermissionCallout = true,
  visitorName,
  preferredName,
}: InputControlsProps) {
  const [visitorNameInputError, setVisitorNameInputError] = useState("");
  const nameInputIsOptional =
    preferredName?.length && preferredName.trim().length > 0;
  const validateAndEnter = () => {
    const visitorNameIsEmpty = !visitorName || visitorName.trim().length === 0;
    if (visitorNameIsEmpty && !nameInputIsOptional) {
      return setVisitorNameInputError(EMPTY_INPUT_ERROR);
    }
    const name = visitorNameIsEmpty ? preferredName : visitorName;
    setVisitorNameInputError("");
    // The name input is only optional when the preferred name is provided, so we know name will be a string
    onEnterWaitingRoom(name!);
  };

  const isErrorBlockingSubmit = visitorNameInputError === EMPTY_INPUT_ERROR;

  const isDisabled =
    shouldDisableSubmitWhenInvalid &&
    (isErrorBlockingSubmit || (!visitorName && !nameInputIsOptional));

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    if (
      visitorNameInputError &&
      (input.trim().length > 0 ||
        !isOverCharacterLimit(input, MAX_NAME_LENGTH) ||
        !containsNonAsciiCharacters(input))
    ) {
      setVisitorNameInputError("");
    }
    if (isOverCharacterLimit(input, MAX_NAME_LENGTH)) {
      setVisitorNameInputError(TOO_LONG_INPUT_ERROR);
    }
    if (shouldWarnForUnsafeCharacters(input)) {
      setVisitorNameInputError(UNSAFE_CHARACTERS_INPUT_ERROR);
    }
    setVisitorName(input);

    onChange?.(event);
  };

  return (
    <div className={twMerge("flex flex-col gap-y-4", className)}>
      <InputControl
        error={visitorNameInputError}
        label={`What name would you like to go by?${nameInputIsOptional ? ` (Optional)` : ""}`}
        className="w-full mb-0"
        required={!nameInputIsOptional}
      >
        <TextInput
          value={visitorName}
          name="visitor-name"
          onChange={onInputChange}
          onEnter={validateAndEnter}
          error={!!visitorNameInputError}
          placeholder="Your name"
        />
      </InputControl>
      <Button
        disabled={isDisabled}
        onClick={validateAndEnter}
        type="submit"
        use="secondary"
        className={twMerge("w-full", buttonClassName)}
      >
        {buttonLabel}
      </Button>
      {showAVPermissionCallout && <AVPermissionCallout />}
    </div>
  );
}

export type InputControlsWrapperProps = WrapperPassThroughProps & {
  showAVPermissionCallout?: boolean;
  submitRequiredAgreements?: (onSuccessfulSubmission: () => void) => void;
  hasPendingRequiredAgreements?: boolean;
};

export function InputControlsWrapper({
  submitRequiredAgreements,
  hasPendingRequiredAgreements,
  ...rest
}: InputControlsWrapperProps) {
  const [visitorName, setVisitorName] = useVisitorName();
  const enterWaitingRoom = useEnterWaitingRoomCallback();
  const { isAuthenticated } = useAuth0();
  const { patientShortId } = useParams<{
    patientShortId: string;
  }>();
  const { data } = useGetCurrentPatientQuery({
    variables: { patientShortId: patientShortId! },
    skip: !isAuthenticated || !patientShortId,
  });
  const preferredName = data?.patientInfo?.preferredName ?? undefined;

  const onEnterWaitingRoom = useCallback(
    (visitorName: string) => {
      if (submitRequiredAgreements && hasPendingRequiredAgreements) {
        const onSuccessfulSubmission = () => enterWaitingRoom(visitorName);
        submitRequiredAgreements(onSuccessfulSubmission);
      } else {
        enterWaitingRoom(visitorName);
      }
    },
    [submitRequiredAgreements, hasPendingRequiredAgreements, enterWaitingRoom],
  );
  return (
    <InputControls
      {...rest}
      onEnterWaitingRoom={onEnterWaitingRoom}
      setVisitorName={setVisitorName}
      visitorName={visitorName}
      preferredName={preferredName}
    />
  );
}
